import { useHttp } from '@/shared/lib'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'

export type CreatePassportTabGroupBody = {
  catalog_invent_objects_type_id: number
  name: string
  open: boolean
  position: 'APPEND' | 'PREPEND'
  mode: 'DEFAULT' | 'EDITABLE' | 'PROTECTED'
  is_tab: boolean
  type: 'GROUP' | 'LIST' | 'HISTORY'
  list_invent_object_type_id?: number | null
}

/**
 * HTTP helpers for passport tab CRUD / reorder. No toasts — callers orchestrate notifications.
 * Save order (see PassportBuilderPage): DELETE removed tabs → POST creates + map refs → PUT renames
 * → PUT reorder (real groups only) → main object-type PUT with `passport_grid_template`.
 */
export function useTabPersistence() {
  const http = useHttp()

  async function createTabGroup(body: CreatePassportTabGroupBody): Promise<number> {
    const res = await http.post('/manuals/object-types/group', body)
    const data = res.data as { group_id?: number } | undefined
    const id = data?.group_id
    if (id == null) throw new Error('createTabGroup: missing group_id in response')
    return id
  }

  function buildUpdateBody(
    group: ObjectTypeGroup,
    patch: Partial<Pick<ObjectTypeGroup, 'name' | 'list_invent_object_type_id'>>,
    catalogTypeFallback: number,
  ): Record<string, unknown> {
    const catalogId = Number(group.catalog_invent_objects_type_id ?? catalogTypeFallback) || catalogTypeFallback
    const listTypeId =
      patch.list_invent_object_type_id !== undefined
        ? patch.list_invent_object_type_id
        : group.list_invent_object_type_id
    return {
      id: group.id,
      catalog_invent_objects_type_id: catalogId,
      name: patch.name ?? group.name,
      open: group.open,
      position: group.position,
      mode: group.mode ?? 'DEFAULT',
      is_tab: group.is_tab,
      type: group.type,
      list_invent_object_type_id: listTypeId,
    }
  }

  async function updateTabGroup(
    group: ObjectTypeGroup,
    patch: Partial<Pick<ObjectTypeGroup, 'name' | 'list_invent_object_type_id'>>,
    catalogTypeFallback: number,
  ) {
    await http.put(
      `/manuals/object-types/group/${group.id}`,
      buildUpdateBody(group, patch, catalogTypeFallback),
    )
  }

  async function reorderTabGroups(group_id: number, target_group_id: number) {
    await http.put('/manuals/object-types/group/reorder', { group_id, target_group_id })
  }

  async function deleteTabGroup(id: number) {
    await http.del(`/manuals/object-types/group/${id}`)
  }

  return {
    createTabGroup,
    updateTabGroup,
    reorderTabGroups,
    deleteTabGroup,
    buildUpdateBody,
  }
}
